import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Toast, useToast, useCommonNavigation } from '../utils';
import {
  DASHBOARD_CARDS,
  getCardsByCategory,
  loadDashboardSettings,
  saveDashboardSettings
} from '../utils/dashboardSettings';
import Sidebar from '../components/Sidebar';

export default function DashboardSettings() {
  const { user } = useAuth();
  const { showToast, toastMessage, hideToast } = useToast();
  const { goToDashboard } = useCommonNavigation();
  const [settings, setSettings] = useState({});
  const cardsByCategory = getCardsByCategory();

  useEffect(() => {
    if (user?.sub) {
      const savedSettings = loadDashboardSettings(user.sub);
      setSettings(savedSettings);
    }
  }, [user?.sub]);

  const handleToggleCard = (cardId) => {
    const newSettings = {
      ...settings,
      [cardId]: !settings[cardId]
    };
    setSettings(newSettings);
    saveDashboardSettings(user.sub, newSettings);
    showToast('Dashboard preferences saved');
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <Sidebar>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-emerald-700">Dashboard Settings</h1>
          <button
            onClick={goToDashboard}
            className="w-full sm:w-auto px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Customize Dashboard Cards</h2>
          <p className="text-gray-600 mb-6">
            Select which cards you would like to see on your dashboard. Changes are saved automatically.
          </p>

          {Object.entries(cardsByCategory).map(([category, cards]) => (
            <div key={category} className="mb-8 last:mb-0">
              <h3 className="text-lg font-medium text-emerald-800 mb-3">{category}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {cards.map(card => (
                  <div
                    key={card.id}
                    className="border rounded-lg p-4 hover:border-emerald-500 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{card.title}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">{card.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={settings[card.id] ?? card.defaultEnabled}
                          onChange={() => handleToggleCard(card.id)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                                      peer-focus:ring-emerald-300 rounded-full peer 
                                      peer-checked:after:translate-x-full peer-checked:after:border-white 
                                      after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                      after:bg-white after:border-gray-300 after:border after:rounded-full 
                                      after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600">
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {toastMessage && <Toast message={toastMessage} onClose={hideToast} />}
    </Sidebar>
  );
} 